import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { flattenMenuManifest } from '../config/menuManifest';
import { buildLoginRedirectPath } from '../utils/authSession';
import {
  buildPagePermissionKey,
  findMenuItemForPath,
  normalizeMenuKey,
  normalizePermissionKey,
} from '../utils/pagePermissions';
const MENU_ACCESS_ALIASES = Object.freeze({
  'pmi mapping log': ['pmi locations'],
});

function ProtectedRoute({ token, children }) {
  const location = useLocation();
  let storedAuthProfile = null;

  try {
    storedAuthProfile = JSON.parse(localStorage.getItem('authProfile') || 'null');
  } catch (_error) {
    storedAuthProfile = null;
  }

  const decodedToken = useMemo(() => {
    if (!token) return {};
    try {
      return jwtDecode(token) || {};
    } catch (_error) {
      return {};
    }
  }, [token]);

  const menuItems = useMemo(() => flattenMenuManifest(), []);
  const routeMenuItem = useMemo(
    () => findMenuItemForPath(location.pathname, menuItems),
    [location.pathname, menuItems]
  );

  const permissionsSource = Array.isArray(storedAuthProfile?.permissions)
    ? storedAuthProfile.permissions
    : decodedToken?.permissions;
  const menuAccessSource = Array.isArray(storedAuthProfile?.menuAccess)
    ? storedAuthProfile.menuAccess
    : decodedToken?.menuAccess;
  const permissions = Array.isArray(permissionsSource)
    ? permissionsSource.map((item) => normalizePermissionKey(item)).filter(Boolean)
    : [];
  const explicitMenuAccessSet = new Set(
    (Array.isArray(menuAccessSource) ? menuAccessSource : []).map((item) => normalizeMenuKey(item)).filter(Boolean)
  );
  const routeMenuKey = routeMenuItem ? normalizeMenuKey(routeMenuItem.key) : '';
  const hasExplicitMenuAccess = explicitMenuAccessSet.size > 0;
  const routeMenuKeysToCheck = routeMenuKey
    ? [routeMenuKey, ...(MENU_ACCESS_ALIASES[routeMenuKey] || []).map((item) => normalizeMenuKey(item))]
    : [];
  const hasMenuAccess =
    !routeMenuKey
    || !hasExplicitMenuAccess
    || routeMenuKeysToCheck.some((item) => explicitMenuAccessSet.has(item));
  const hasGranularViewPermissions = permissions.some((item) => /^page\..+\.view$/.test(item));
  const requiredViewPermissions = routeMenuItem
    ? [
      routeMenuItem.key,
      ...(MENU_ACCESS_ALIASES[routeMenuKey] || []),
    ]
      .map((item) => normalizePermissionKey(buildPagePermissionKey(item, 'view')))
      .filter(Boolean)
    : [];
  const requiredEditPermissions = routeMenuItem
    ? [
      routeMenuItem.key,
      ...(MENU_ACCESS_ALIASES[routeMenuKey] || []),
    ]
      .map((item) => normalizePermissionKey(buildPagePermissionKey(item, 'edit')))
      .filter(Boolean)
    : [];
  const hasAccess =
    requiredViewPermissions.length === 0 ||
    requiredViewPermissions.some((permission) => permissions.includes(permission)) ||
    requiredEditPermissions.some((permission) => permissions.includes(permission));

  if (!token) {
    const intendedPath = `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
    return (
      <Navigate
        to={buildLoginRedirectPath(intendedPath)}
        replace
        state={{ from: location }}
      />
    );
  }

  if (!hasMenuAccess) {
    return (
      <div style={{ padding: '32px 20px' }}>
        <h2 style={{ margin: 0, marginBottom: 8 }}>Access Denied</h2>
        <p style={{ margin: 0, color: '#4b5563' }}>
          This page is not enabled for your current role configuration.
        </p>
      </div>
    );
  }

  if (hasGranularViewPermissions && requiredViewPermissions.length > 0 && !hasAccess) {
    return (
      <div style={{ padding: '32px 20px' }}>
        <h2 style={{ margin: 0, marginBottom: 8 }}>Access Denied</h2>
        <p style={{ margin: 0, color: '#4b5563' }}>
          You do not have `view` permission for this page.
        </p>
      </div>
    );
  }

  return children;
}

export default ProtectedRoute;
